import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type {
  BenchmarkInsightCompetitorsCompetitor,
  BenchmarkInsightCompetitorsCompset,
  BenchmarkInsightCompetitorsDesiredCompetitor,
} from 'lumen/models';
import type HotelSettingsCompetitorsBenchmarkCompsetRoute from 'lumen/routes/hotel/settings/competitors/benchmark/compset';
import { ROUTE_BENCHMARK_INSIGHT_COMPSET_BUILDER } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelSettingsCompetitorsBenchmarkCompsetController extends Controller {
  declare model: ModelFrom<HotelSettingsCompetitorsBenchmarkCompsetRoute>;

  @service declare access: Services['access'];
  @service('benchmark-insight/competitors/desired-compset')
  declare desiredCompsetService: Services['benchmark-insight/competitors/desired-compset'];

  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  get competitors(): BenchmarkInsightCompetitorsCompetitor[] {
    if (!this.compset) {
      return [];
    }

    return [...this.compset.compset];
  }

  get compset(): BenchmarkInsightCompetitorsCompset | null {
    return this.model.compset.last?.value ?? null;
  }

  get emptyStateDescription(): string {
    if (!this.access.hasBenchmarkInsightSettingsCompsetEdit) {
      return '';
    }

    return this.intl.t(
      'settings.competitors.benchmark.compset.empty_state.description',
    );
  }

  get emptyStateTitle(): string {
    if (!this.access.hasBenchmarkInsightSettingsCompsetEdit) {
      return this.intl.t(
        'settings.competitors.benchmark.compset.empty_state.title.viewer',
      );
    }

    return this.intl.t(
      'settings.competitors.benchmark.compset.empty_state.title',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNumberOfCompetitors() {
    return this.intl.t('benchmark_insight.count_hotels', {
      numberOfHotels: this.competitors.length,
      maxNumberOfHotels: this.compset?.MAX_COMPETITORS,
    });
  }

  get isLoading(): boolean {
    return this.model.compset.isRunning;
  }

  get newCompetitorAvailableBannerMessage(): string {
    const availableCompetitorsCount = this.unusedDesiredCompetitors.filter(
      (competitor) => competitor.compsetAvailable,
    ).length;

    if (!this.access.hasBenchmarkInsightSettingsCompsetEdit) {
      return this.intl.t(
        'settings.competitors.benchmark.compset.banner.new_competitor_available.viewer',
        {
          count: availableCompetitorsCount,
        },
      );
    }

    return this.intl.t(
      'settings.competitors.benchmark.compset.banner.new_competitor_available',
      { count: availableCompetitorsCount },
    );
  }

  get showEmptyState(): boolean {
    return !this.isLoading && !this.competitors.length;
  }

  get showInvalidCompsetBanner(): boolean {
    if (!this.compset) {
      return false;
    }

    return !this.compset.isCompsetValid;
  }

  get showNewCompetitorAvailableBanner(): boolean {
    return this.unusedDesiredCompetitors.some(
      (competitor) => competitor.compsetAvailable,
    );
  }

  get unusedDesiredCompetitors(): BenchmarkInsightCompetitorsDesiredCompetitor[] {
    if (!this.compset) {
      return [];
    }

    return this.desiredCompsetService.getUnusedDesiredCompetitors([
      this.compset,
    ]);
  }

  get unusedDesiredCompetitorsTooltipText(): string {
    const isEveryUnusedDesiredCompetitorsAvailable =
      this.unusedDesiredCompetitors.every(
        (competitor) => competitor.compsetAvailable,
      );

    if (isEveryUnusedDesiredCompetitorsAvailable) {
      return this.intl.t(
        'settings.competitors.benchmark.compset.not_used.all_available',
      );
    }

    return this.intl.t(
      'settings.competitors.benchmark.compset.builder.not_used.tooltip',
    );
  }

  @action onClickCreateCompset(): void {
    this.router.transitionTo(ROUTE_BENCHMARK_INSIGHT_COMPSET_BUILDER);
  }
}
